// ─────────────────────────────────────────────────────────────────────────────
// Dashboard mock data
//
// Used when DASHBOARD_USE_MOCK_DATA feature flag is ON.
// Mirrors the exact shape returned by the real API endpoints:
//   - GET dashboard/stats          → mockStats
//   - POST dashboard/performace-trend → mockTrendData
//
// Remove this file once the feature flag is turned off permanently.
// ─────────────────────────────────────────────────────────────────────────────

// ── Stats (mirrors getDashboardStats response) ────────────────────────────
export const mockStats = {
  totalQuizzes: 47,
  overallAccuracy: 68.4,
  averageScore: 72.1,
  strongestSpeciality: "Cardiology",
  weakestSpeciality: "Anatomy",
};

// ── Trend data (mirrors getPerformanceTrend response) ─────────────────────

// Accuracy trend — ~3 months of data, upward trajectory with realistic dips.
// Each entry represents one quiz attempt.
// correct + incorrect = total questions answered in that attempt.
// Dates are hardcoded so the streak / 7-day dots render correctly relative to
// today's real clock. Adjust the most-recent entries if needed.
export const mockAccuracyTrend = [
  // January 2026 — early, lower accuracy
  { date: "2026-01-10T08:00:00.000Z", accuracy: "42.0", correct: 11, incorrect: 15 },
  { date: "2026-01-15T08:00:00.000Z", accuracy: "38.5", correct: 10, incorrect: 16 },
  { date: "2026-01-20T08:00:00.000Z", accuracy: "51.0", correct: 14, incorrect: 14 },
  { date: "2026-01-24T08:00:00.000Z", accuracy: "48.3", correct: 12, incorrect: 13 },
  { date: "2026-01-28T08:00:00.000Z", accuracy: "55.0", correct: 17, incorrect: 14 },

  // February 2026 — building consistency
  { date: "2026-02-02T08:00:00.000Z", accuracy: "57.2", correct: 17, incorrect: 13 },
  { date: "2026-02-05T08:00:00.000Z", accuracy: "62.0", correct: 19, incorrect: 12 },
  { date: "2026-02-08T08:00:00.000Z", accuracy: "58.7", correct: 16, incorrect: 11 },
  { date: "2026-02-12T08:00:00.000Z", accuracy: "64.4", correct: 17, incorrect:  9 },
  { date: "2026-02-15T08:00:00.000Z", accuracy: "61.0", correct: 17, incorrect: 11 },
  { date: "2026-02-19T08:00:00.000Z", accuracy: "67.5", correct: 20, incorrect: 10 },
  { date: "2026-02-22T08:00:00.000Z", accuracy: "63.8", correct: 19, incorrect: 11 },
  { date: "2026-02-26T08:00:00.000Z", accuracy: "70.0", correct: 21, incorrect:  9 },

  // March 2026 — accelerating improvement
  { date: "2026-03-01T08:00:00.000Z", accuracy: "68.3", correct: 20, incorrect:  9 },
  { date: "2026-03-05T08:00:00.000Z", accuracy: "71.0", correct: 22, incorrect:  9 },
  { date: "2026-03-08T08:00:00.000Z", accuracy: "73.2", correct: 22, incorrect:  8 },
  { date: "2026-03-12T08:00:00.000Z", accuracy: "69.5", correct: 21, incorrect:  9 },
  { date: "2026-03-15T08:00:00.000Z", accuracy: "75.0", correct: 24, incorrect:  8 },
  { date: "2026-03-18T08:00:00.000Z", accuracy: "72.4", correct: 21, incorrect:  8 },
  { date: "2026-03-22T08:00:00.000Z", accuracy: "77.1", correct: 27, incorrect:  8 },
  { date: "2026-03-25T08:00:00.000Z", accuracy: "74.6", correct: 26, incorrect:  9 },
  { date: "2026-03-28T08:00:00.000Z", accuracy: "79.3", correct: 26, incorrect:  7 },
  { date: "2026-03-31T08:00:00.000Z", accuracy: "76.0", correct: 26, incorrect:  8 },

  // April 2026 — peak performance, active streak
  { date: "2026-04-02T08:00:00.000Z", accuracy: "78.6", correct: 28, incorrect:  8 },
  { date: "2026-04-05T08:00:00.000Z", accuracy: "81.1", correct: 30, incorrect:  7 },
  { date: "2026-04-07T08:00:00.000Z", accuracy: "79.2", correct: 28, incorrect:  7 },
  { date: "2026-04-09T08:00:00.000Z", accuracy: "83.0", correct: 29, incorrect:  6 },
  { date: "2026-04-10T08:00:00.000Z", accuracy: "85.7", correct: 30, incorrect:  5 },
  { date: "2026-04-11T08:00:00.000Z", accuracy: "82.0", correct: 30, incorrect:  6 },
  { date: "2026-04-12T08:00:00.000Z", accuracy: "84.2", correct: 32, incorrect:  6 },
  { date: "2026-04-13T08:00:00.000Z", accuracy: "83.3", correct: 30, incorrect:  6 },
];

// Performance by specialty — correct vs incorrect totals
export const mockSpecialityPerformance = {
  Cardiology:   { correct: 31, incorrect: 6  },  // ~83.8%
  Physiology:   { correct: 19, incorrect: 8  },  // ~70.4%
  Pharmacology: { correct: 22, incorrect: 11 },  // ~66.7%
  Neurology:    { correct: 12, incorrect: 7  },  // ~63.2%
  Pathology:    { correct: 14, incorrect: 14 },  // ~50.0%
  Anatomy:      { correct: 9,  incorrect: 18 },  // ~33.3%
};

// Latest quizzes — 10 most recent attempts
export const mockLatestQuizzes = [
  { quizScriptName: "Cardiac Physiology — Module 3",   date: "2026-04-13T08:00:00.000Z", accuracy: "83.3" },
  { quizScriptName: "Drug Interactions Review",         date: "2026-04-12T08:00:00.000Z", accuracy: "84.2" },
  { quizScriptName: "Heart Failure & Management",       date: "2026-04-11T08:00:00.000Z", accuracy: "82.0" },
  { quizScriptName: "Renal Pathophysiology",            date: "2026-04-10T08:00:00.000Z", accuracy: "85.7" },
  { quizScriptName: "Pharmacokinetics Deep Dive",       date: "2026-04-09T08:00:00.000Z", accuracy: "83.0" },
  { quizScriptName: "Pulmonary Disorders",              date: "2026-04-07T08:00:00.000Z", accuracy: "79.2" },
  { quizScriptName: "Endocrine System Review",          date: "2026-04-05T08:00:00.000Z", accuracy: "81.1" },
  { quizScriptName: "Liver & Hepatic Pathology",        date: "2026-04-02T08:00:00.000Z", accuracy: "78.6" },
  { quizScriptName: "Neurology Essentials",             date: "2026-03-31T08:00:00.000Z", accuracy: "76.0" },
  { quizScriptName: "Musculoskeletal Disorders",        date: "2026-03-28T08:00:00.000Z", accuracy: "79.4" },
];

// Assembled shape matching POST /dashboard/v2/performace-trend response
// (used by KPI enrichData, Analytics row, Quizzes table)
export const mockTrendData = {
  accuracyTrend:          mockAccuracyTrend,
  specialityPerformance:  mockSpecialityPerformance,
  latestQuizzes:          mockLatestQuizzes,
};

// Activity heatmap — one entry per active day, last 12 months.
// Matches POST /dashboard/v2/activity-heatmap response shape.
// date is "YYYY-MM-DD" (no time), quizCount is integer, avgAccuracy is float.
export const mockActivityHeatmap = [
  // Apr 2025 — early days, low accuracy
  { date: "2025-04-07", quizCount: 1, avgAccuracy: 38.5 },
  { date: "2025-04-09", quizCount: 2, avgAccuracy: 41.0 },
  { date: "2025-04-11", quizCount: 1, avgAccuracy: 39.2 },
  { date: "2025-04-14", quizCount: 2, avgAccuracy: 43.5 },
  { date: "2025-04-16", quizCount: 1, avgAccuracy: 40.8 },
  { date: "2025-04-21", quizCount: 2, avgAccuracy: 44.3 },
  { date: "2025-04-24", quizCount: 1, avgAccuracy: 42.0 },
  { date: "2025-04-28", quizCount: 2, avgAccuracy: 45.7 },
  // May 2025 — building habits
  { date: "2025-05-02", quizCount: 2, avgAccuracy: 47.5 },
  { date: "2025-05-05", quizCount: 1, avgAccuracy: 44.8 },
  { date: "2025-05-07", quizCount: 2, avgAccuracy: 48.3 },
  { date: "2025-05-09", quizCount: 1, avgAccuracy: 46.5 },
  { date: "2025-05-12", quizCount: 2, avgAccuracy: 50.2 },
  { date: "2025-05-14", quizCount: 1, avgAccuracy: 47.8 },
  { date: "2025-05-19", quizCount: 2, avgAccuracy: 51.5 },
  { date: "2025-05-21", quizCount: 1, avgAccuracy: 49.0 },
  { date: "2025-05-26", quizCount: 2, avgAccuracy: 52.3 },
  { date: "2025-05-29", quizCount: 1, avgAccuracy: 50.8 },
  // Jun 2025
  { date: "2025-06-02", quizCount: 2, avgAccuracy: 53.4 },
  { date: "2025-06-05", quizCount: 1, avgAccuracy: 51.2 },
  { date: "2025-06-09", quizCount: 2, avgAccuracy: 55.0 },
  { date: "2025-06-11", quizCount: 1, avgAccuracy: 52.7 },
  { date: "2025-06-16", quizCount: 2, avgAccuracy: 57.3 },
  { date: "2025-06-18", quizCount: 1, avgAccuracy: 54.5 },
  { date: "2025-06-23", quizCount: 2, avgAccuracy: 58.1 },
  { date: "2025-06-25", quizCount: 1, avgAccuracy: 56.4 },
  { date: "2025-06-30", quizCount: 2, avgAccuracy: 59.0 },
  // Jul 2025 — crossing 60% threshold
  { date: "2025-07-02", quizCount: 2, avgAccuracy: 60.5 },
  { date: "2025-07-04", quizCount: 1, avgAccuracy: 58.3 },
  { date: "2025-07-07", quizCount: 2, avgAccuracy: 62.1 },
  { date: "2025-07-09", quizCount: 1, avgAccuracy: 59.8 },
  { date: "2025-07-11", quizCount: 2, avgAccuracy: 63.4 },
  { date: "2025-07-14", quizCount: 3, avgAccuracy: 61.0 },
  { date: "2025-07-16", quizCount: 1, avgAccuracy: 64.2 },
  { date: "2025-07-21", quizCount: 2, avgAccuracy: 62.5 },
  { date: "2025-07-23", quizCount: 1, avgAccuracy: 65.0 },
  { date: "2025-07-28", quizCount: 2, avgAccuracy: 63.8 },
  { date: "2025-07-30", quizCount: 1, avgAccuracy: 66.1 },
  // Aug 2025 — summer dip
  { date: "2025-08-04", quizCount: 1, avgAccuracy: 62.3 },
  { date: "2025-08-07", quizCount: 2, avgAccuracy: 64.5 },
  { date: "2025-08-11", quizCount: 1, avgAccuracy: 61.8 },
  { date: "2025-08-14", quizCount: 2, avgAccuracy: 65.2 },
  { date: "2025-08-20", quizCount: 1, avgAccuracy: 63.4 },
  { date: "2025-08-25", quizCount: 2, avgAccuracy: 66.0 },
  { date: "2025-08-27", quizCount: 1, avgAccuracy: 64.7 },
  { date: "2025-08-29", quizCount: 2, avgAccuracy: 67.3 },
  // Sep 2025 — picking back up
  { date: "2025-09-01", quizCount: 2, avgAccuracy: 66.5 },
  { date: "2025-09-03", quizCount: 1, avgAccuracy: 68.2 },
  { date: "2025-09-05", quizCount: 2, avgAccuracy: 67.0 },
  { date: "2025-09-08", quizCount: 1, avgAccuracy: 69.4 },
  { date: "2025-09-10", quizCount: 2, avgAccuracy: 68.1 },
  { date: "2025-09-12", quizCount: 1, avgAccuracy: 70.5 },
  { date: "2025-09-15", quizCount: 2, avgAccuracy: 69.3 },
  { date: "2025-09-17", quizCount: 1, avgAccuracy: 71.0 },
  { date: "2025-09-19", quizCount: 2, avgAccuracy: 70.2 },
  { date: "2025-09-22", quizCount: 1, avgAccuracy: 72.1 },
  { date: "2025-09-24", quizCount: 2, avgAccuracy: 71.5 },
  { date: "2025-09-29", quizCount: 1, avgAccuracy: 73.0 },
  // Oct 2025 — solid consistency
  { date: "2025-10-01", quizCount: 2, avgAccuracy: 72.4 },
  { date: "2025-10-03", quizCount: 1, avgAccuracy: 74.0 },
  { date: "2025-10-06", quizCount: 2, avgAccuracy: 73.1 },
  { date: "2025-10-08", quizCount: 1, avgAccuracy: 75.2 },
  { date: "2025-10-10", quizCount: 2, avgAccuracy: 74.5 },
  { date: "2025-10-13", quizCount: 3, avgAccuracy: 73.8 },
  { date: "2025-10-15", quizCount: 1, avgAccuracy: 76.0 },
  { date: "2025-10-17", quizCount: 2, avgAccuracy: 75.3 },
  { date: "2025-10-20", quizCount: 1, avgAccuracy: 74.1 },
  { date: "2025-10-22", quizCount: 2, avgAccuracy: 76.5 },
  { date: "2025-10-24", quizCount: 1, avgAccuracy: 75.8 },
  { date: "2025-10-27", quizCount: 2, avgAccuracy: 77.2 },
  { date: "2025-10-29", quizCount: 1, avgAccuracy: 76.0 },
  // Nov 2025
  { date: "2025-11-03", quizCount: 2, avgAccuracy: 76.4 },
  { date: "2025-11-05", quizCount: 1, avgAccuracy: 77.8 },
  { date: "2025-11-07", quizCount: 2, avgAccuracy: 75.9 },
  { date: "2025-11-10", quizCount: 1, avgAccuracy: 78.3 },
  { date: "2025-11-12", quizCount: 2, avgAccuracy: 77.0 },
  { date: "2025-11-14", quizCount: 1, avgAccuracy: 79.1 },
  { date: "2025-11-17", quizCount: 2, avgAccuracy: 78.4 },
  { date: "2025-11-19", quizCount: 1, avgAccuracy: 77.5 },
  { date: "2025-11-21", quizCount: 2, avgAccuracy: 79.8 },
  { date: "2025-11-24", quizCount: 1, avgAccuracy: 78.9 },
  // Dec 2025 — holiday slowdown
  { date: "2025-12-01", quizCount: 2, avgAccuracy: 79.2 },
  { date: "2025-12-03", quizCount: 1, avgAccuracy: 77.8 },
  { date: "2025-12-08", quizCount: 2, avgAccuracy: 80.1 },
  { date: "2025-12-10", quizCount: 1, avgAccuracy: 78.5 },
  { date: "2025-12-15", quizCount: 2, avgAccuracy: 79.6 },
  { date: "2025-12-17", quizCount: 1, avgAccuracy: 81.0 },
  { date: "2025-12-22", quizCount: 1, avgAccuracy: 79.3 },
  // Jan 2026
  { date: "2026-01-05", quizCount: 2, avgAccuracy: 80.4 },
  { date: "2026-01-07", quizCount: 1, avgAccuracy: 79.1 },
  { date: "2026-01-10", quizCount: 2, avgAccuracy: 81.2 },
  { date: "2026-01-15", quizCount: 1, avgAccuracy: 80.0 },
  { date: "2026-01-20", quizCount: 2, avgAccuracy: 79.5 },
  { date: "2026-01-24", quizCount: 1, avgAccuracy: 81.8 },
  { date: "2026-01-28", quizCount: 2, avgAccuracy: 80.6 },
  { date: "2026-01-30", quizCount: 1, avgAccuracy: 82.3 },
  // Feb 2026
  { date: "2026-02-02", quizCount: 2, avgAccuracy: 81.5 },
  { date: "2026-02-05", quizCount: 1, avgAccuracy: 83.0 },
  { date: "2026-02-08", quizCount: 2, avgAccuracy: 82.1 },
  { date: "2026-02-10", quizCount: 1, avgAccuracy: 80.4 },
  { date: "2026-02-12", quizCount: 2, avgAccuracy: 83.6 },
  { date: "2026-02-15", quizCount: 1, avgAccuracy: 82.0 },
  { date: "2026-02-17", quizCount: 2, avgAccuracy: 84.1 },
  { date: "2026-02-19", quizCount: 1, avgAccuracy: 82.8 },
  { date: "2026-02-22", quizCount: 2, avgAccuracy: 83.4 },
  { date: "2026-02-24", quizCount: 1, avgAccuracy: 85.0 },
  { date: "2026-02-26", quizCount: 2, avgAccuracy: 83.9 },
  // Mar 2026
  { date: "2026-03-02", quizCount: 2, avgAccuracy: 84.5 },
  { date: "2026-03-05", quizCount: 1, avgAccuracy: 83.1 },
  { date: "2026-03-08", quizCount: 2, avgAccuracy: 85.3 },
  { date: "2026-03-10", quizCount: 1, avgAccuracy: 84.0 },
  { date: "2026-03-12", quizCount: 3, avgAccuracy: 82.7 },
  { date: "2026-03-15", quizCount: 2, avgAccuracy: 85.8 },
  { date: "2026-03-18", quizCount: 1, avgAccuracy: 84.6 },
  { date: "2026-03-22", quizCount: 2, avgAccuracy: 86.1 },
  { date: "2026-03-25", quizCount: 1, avgAccuracy: 85.2 },
  { date: "2026-03-28", quizCount: 2, avgAccuracy: 84.8 },
  { date: "2026-03-30", quizCount: 1, avgAccuracy: 86.5 },
  { date: "2026-03-31", quizCount: 2, avgAccuracy: 85.0 },
  // Apr 2026
  { date: "2026-04-02", quizCount: 2, avgAccuracy: 85.7 },
  { date: "2026-04-05", quizCount: 1, avgAccuracy: 86.8 },
  { date: "2026-04-07", quizCount: 2, avgAccuracy: 84.9 },
  { date: "2026-04-09", quizCount: 1, avgAccuracy: 87.3 },
  { date: "2026-04-10", quizCount: 2, avgAccuracy: 86.0 },
  { date: "2026-04-11", quizCount: 1, avgAccuracy: 85.5 },
  { date: "2026-04-12", quizCount: 2, avgAccuracy: 87.8 },
  { date: "2026-04-13", quizCount: 1, avgAccuracy: 86.2 },
];

// Performance by attribute — matches POST /dashboard/v2/performance-by-attribute
// Each key maps to the `performanceBy` request field value.
// items shape: { name, accuracy, correct, incorrect, total }
export const mockPerformanceByAttribute = {
  specialty: [
    { name: "Cardiology",   accuracy: 84, correct: 31, incorrect: 6,  total: 37 },
    { name: "Physiology",   accuracy: 70, correct: 19, incorrect: 8,  total: 27 },
    { name: "Pharmacology", accuracy: 67, correct: 22, incorrect: 11, total: 33 },
    { name: "Neurology",    accuracy: 63, correct: 12, incorrect: 7,  total: 19 },
    { name: "Pathology",    accuracy: 50, correct: 14, incorrect: 14, total: 28 },
    { name: "Anatomy",      accuracy: 33, correct: 9,  incorrect: 18, total: 27 },
  ],
  script: [
    { name: "Cardiac Physiology — Module 3",  accuracy: 83, correct: 28, incorrect: 6,  total: 34 },
    { name: "Drug Interactions Review",        accuracy: 84, correct: 26, incorrect: 5,  total: 31 },
    { name: "Heart Failure & Management",      accuracy: 82, correct: 24, incorrect: 5,  total: 29 },
    { name: "Renal Pathophysiology",           accuracy: 86, correct: 25, incorrect: 4,  total: 29 },
    { name: "Pharmacokinetics Deep Dive",      accuracy: 83, correct: 22, incorrect: 4,  total: 26 },
    { name: "Pulmonary Disorders",             accuracy: 79, correct: 19, incorrect: 5,  total: 24 },
    { name: "Endocrine System Review",         accuracy: 81, correct: 20, incorrect: 5,  total: 25 },
    { name: "Neurology Essentials",            accuracy: 76, correct: 16, incorrect: 5,  total: 21 },
    { name: "Musculoskeletal Disorders",       accuracy: 79, correct: 15, incorrect: 4,  total: 19 },
  ],
  topic: [
    { name: "Cardiovascular",  accuracy: 82, correct: 51, incorrect: 11, total: 62 },
    { name: "Pharmacology",    accuracy: 74, correct: 38, incorrect: 13, total: 51 },
    { name: "Neuroscience",    accuracy: 65, correct: 28, incorrect: 15, total: 43 },
    { name: "Pathophysiology", accuracy: 58, correct: 24, incorrect: 17, total: 41 },
    { name: "Anatomy",         accuracy: 44, correct: 18, incorrect: 23, total: 41 },
  ],
  explanation: [
    { name: "Heart Failure Mechanism",   accuracy: 80, correct: 24, incorrect: 6,  total: 30 },
    { name: "Drug Metabolism",           accuracy: 76, correct: 19, incorrect: 6,  total: 25 },
    { name: "Neural Pathways",           accuracy: 67, correct: 14, incorrect: 7,  total: 21 },
    { name: "Renal Filtration",          accuracy: 71, correct: 15, incorrect: 6,  total: 21 },
    { name: "Hormone Regulation",        accuracy: 62, correct: 13, incorrect: 8,  total: 21 },
    { name: "Immune Response",           accuracy: 55, correct: 11, incorrect: 9,  total: 20 },
    { name: "Joint Structure & Disease", accuracy: 48, correct: 10, incorrect: 11, total: 21 },
  ],
};

// Score distribution — all quiz attempts, matches POST /dashboard/v2/score-distribution
// accuracy is a number (not string), quizScriptName cycles through known scripts
const SCRIPT_NAMES = [
  "Cardiac Physiology — Module 3",
  "Drug Interactions Review",
  "Heart Failure & Management",
  "Renal Pathophysiology",
  "Pharmacokinetics Deep Dive",
  "Pulmonary Disorders",
  "Endocrine System Review",
  "Liver & Hepatic Pathology",
  "Neurology Essentials",
  "Musculoskeletal Disorders",
];

export const mockScoreDistribution = {
  quizzes: mockAccuracyTrend.map((entry, i) => ({
    date:           entry.date,
    accuracy:       parseFloat(entry.accuracy),
    quizScriptName: SCRIPT_NAMES[i % SCRIPT_NAMES.length],
  })),
};

// Assembled shape matching POST /dashboard/v2/performance-chart response
// (used only by the Overall Performance chart)
// accuracyTrend here includes correct + incorrect per entry
export const mockChartData = {
  accuracyTrend: mockAccuracyTrend,
};
